// pages/CrewMembers.js
import React, { useState, useEffect } from 'react';
import { ContentLayout, PageContainer } from '../components/layout';
import { Button } from '../components/common';
import { CrewMemberModal } from '../components/modals/CrewMemberModal';
import { useCrewMembersPG } from '../hooks/useCrewMembersPG';
import { useServicesPG } from '../hooks/useServicesPG';
import { useTranslations } from '../hooks/useTranslations';
import { UserCheck } from 'lucide-react';

export function CrewMembers({ onNavigate }) {
  const t = useTranslations();
  const {
    crewMembers,
    loading,
    error,
    handleCrewMemberSubmit,
    deleteCrewMember,
    updateCrewMember,
    searchCrewMembers
  } = useCrewMembersPG();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCrewMember, setSelectedCrewMember] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [filteredCrewMembers, setFilteredCrewMembers] = useState([]);

  const { services } = useServicesPG();

  // Filter crew members based on search and active status
  useEffect(() => {
    let filtered = searchTerm ? searchCrewMembers(searchTerm) : crewMembers;
    if (!showInactive) {
      filtered = filtered.filter(member => member.is_active);
    }
    setFilteredCrewMembers(filtered);
  }, [crewMembers, searchTerm, showInactive]);

  const handleAddCrewMember = () => {
    setSelectedCrewMember(null);
    setModalMode('add');
    setIsModalOpen(true);
  };

  const handleEditCrewMember = (crewMember) => {
    setSelectedCrewMember(crewMember);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleModalSuccess = async (crewMemberDataOrId, modeOrData, mode) => {
    try {
      await handleCrewMemberSubmit(crewMemberDataOrId, modeOrData, mode);
    } catch (error) {
      throw error; // Re-throw to let modal handle the error display
    }
  };

  const handleDeactivate = async (crewMemberId) => {
    if (window.confirm(t.areYouSureDeactivate)) {
      try {
        await deleteCrewMember(crewMemberId);
      } catch (error) {
        alert(t.errorDeactivatingCrewMember + ': ' + error.message);
      }
    }
  };

  const handleActivate = async (crewMemberId) => {
    try {
      await updateCrewMember(crewMemberId, { is_active: true });
    } catch (error) {
      alert(t.errorActivatingCrewMember + ': ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
        <p>{t.errorLoadingCrewMembers}: {error.message}</p>
      </div>
    );
  }

  return (
    <ContentLayout
      currentPage="crew-members"
      onNavigate={onNavigate}
      title={t.crewMembers}
      description={t.manageYourTeamMembersAndSpecialties}
      primaryAction={{
        label: t.addCrewMember,
        onClick: () => handleAddCrewMember(),
        icon: <UserCheck className="w-4 h-4" /> 
      }}
    >
      <PageContainer>
        <div className="space-y-6">

          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder={t.searchCrewMembers}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                />
              </div>
              <div className="flex items-center mx-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="h-4 w-4 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                  />
                  <span className="mr-2 text-sm text-gray-700">{t.showInactive}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Crew Members List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {filteredCrewMembers.length === 0 ? (
              <div className="p-12 text-center">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t.noCrewMembersFound}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm ? t.tryAdjustingSearchCriteria : t.getStartedByAddingFirstCrewMember}
                </p>
                {!searchTerm && (
                  <div className="mt-6">
                    <Button onClick={handleAddCrewMember}>{t.addCrewMember}</Button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* ---- Mobile: compact card list ---- */}
                <ul className="md:hidden divide-y divide-gray-100" dir="rtl">
                  {filteredCrewMembers.map((member) => {
                    const serviceNames =
                      member.assigned_services?.map((id) => {
                        const s = services.find((x) => x.id === id);
                        return s ? s.name : t.unknownService;
                      }) ?? [];

                    return (
                      <li key={member.id} className="p-3">
                        <div className="flex items-start gap-3">
                          {/* Avatar */}
                          <div
                            className="h-10 w-10 rounded-full flex items-center justify-center text-white text-sm font-medium shadow-sm"
                            style={{ backgroundColor: member.color }}
                            aria-hidden="true"
                          >
                            {member.getInitials()}
                          </div>

                          <div className="min-w-0 flex-1">
                            {/* Name + status */}
                            <div className="flex items-center justify-between gap-2">
                              <h4 className="truncate text-gray-900 font-semibold text-base">{member.name}</h4>
                              <span
                                className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${member.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}
                              >
                                {member.is_active ? t.active : t.inactive}
                              </span>
                            </div>

                            {/* Contact (one line, compact) */}
                            <div className="mt-1 flex items-center gap-2 text-[12px] text-gray-600">
                              <a
                                href={member.phone ? `tel:${member.phone}` : undefined}
                                className="truncate hover:text-gray-800"
                                title={member.phone || t.noPhone}
                              >
                                📞 {member.phone || t.noPhone}
                              </a>
                              <span className="text-gray-300">•</span>
                              <a
                                href={member.email ? `mailto:${member.email}` : undefined}
                                className="truncate hover:text-gray-800"
                                title={member.email || t.noEmail}
                              >
                                ✉️ {member.email || t.noEmail}
                              </a>
                            </div>

                            {/* Services (wrap) */}
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {serviceNames.length ? (
                                serviceNames.map((name, i) => (
                                  <span
                                    key={`${member.id}-svc-${i}`}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100"
                                  >
                                    {name}
                                  </span>
                                ))
                              ) : (
                                <span className="text-[11px] text-gray-500">{t.noAssignedServices}</span>
                              )}
                            </div>

                            {/* Actions row */}
                            <div className="mt-2 flex items-center justify-between">
                              {/* Quick actions (icons) */}
                              <div className="flex items-center gap-1.5">
                                {member.phone && (
                                  <a
                                    href={`tel:${member.phone}`}
                                    className="h-9 w-9 rounded-xl bg-gray-50 border border-gray-200 hover:bg-gray-100 grid place-items-center shadow-sm"
                                    aria-label={t.call || 'Call'}
                                    title={t.call || 'Call'}
                                  >
                                    📞
                                  </a>
                                )}
                                {member.email && (
                                  <a
                                    href={`mailto:${member.email}`}
                                    className="h-9 w-9 rounded-xl bg-gray-50 border border-gray-200 hover:bg-gray-100 grid place-items-center shadow-sm"
                                    aria-label={t.email || 'Email'}
                                    title={t.email || 'Email'}
                                  >
                                    ✉️
                                  </a>
                                )}
                              </div>

                              {/* Edit + Activate/Deactivate */}
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleEditCrewMember(member)}
                                  className="text-emerald-700 hover:text-emerald-900 text-sm font-medium"
                                >
                                  {t.edit}
                                </button>
                                {member.is_active ? (
                                  <button
                                    onClick={() => handleDeactivate(member.id)}
                                    className="text-red-600 hover:text-red-700 text-sm font-medium"
                                  >
                                    {t.deactivate}
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleActivate(member.id)}
                                    className="text-green-600 hover:text-green-700 text-sm font-medium"
                                  >
                                    {t.activate}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>

                {/* ---- Desktop / Tablet: keep table ---- */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200" dir="rtl">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t.member}
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t.contact}
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t.assignedServices}
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t.status}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t.actions}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCrewMembers.map((member) => (
                        <tr key={member.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div
                                className="h-10 w-10 rounded-full flex items-center justify-center text-white font-medium text-sm"
                                style={{ backgroundColor: member.color }}
                              >
                                {member.getInitials()}
                              </div>
                              <div className="mr-4">
                                <div className="text-sm font-medium text-gray-900">{member.name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="text-sm text-gray-900">{member.email || t.noEmail}</div>
                            <div className="text-sm text-gray-500">{member.phone || t.noPhone}</div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex flex-wrap gap-1 justify-end">
                              {member.assigned_services?.length ? (
                                member.assigned_services.map((serviceId) => {
                                  const service = services.find((s) => s.id === serviceId);
                                  return (
                                    <span
                                      key={serviceId}
                                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                      {service ? service.name : t.unknownService}
                                    </span>
                                  );
                                })
                              ) : (
                                <span className="text-xs text-gray-500">{t.noAssignedServices}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${member.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}
                            >
                              {member.is_active ? t.active : t.inactive}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                            <div className="flex justify-start space-x-2">
                              <button
                                onClick={() => handleEditCrewMember(member)}
                                className="text-indigo-600 hover:text-indigo-900 mx-3"
                              >
                                {t.edit}
                              </button>
                              {member.is_active ? (
                                <button onClick={() => handleDeactivate(member.id)} className="text-red-600 hover:text-red-900">
                                  {t.deactivate}
                                </button>
                              ) : (
                                <button onClick={() => handleActivate(member.id)} className="text-green-600 hover:text-green-900">
                                  {t.activate}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

        </div>
      </PageContainer>

      {/* Crew Member Modal */}
      {isModalOpen && (
        <CrewMemberModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={handleModalSuccess}
          mode={modalMode}
          crewMember={selectedCrewMember}
        />
      )}
    </ContentLayout>
  );
}